import { useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { getApi } from '../api';
import { formatCalendarDate, SIMPLE_DATE_FORMAT } from '../utils/utils';
import { transformSnake2Camel } from './utils';

const isValidDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const start = moment(startDate, SIMPLE_DATE_FORMAT, true);
  const end = moment(endDate, SIMPLE_DATE_FORMAT, true);

  return start.isValid() && end.isValid() && start.isSameOrBefore(end);
};

export const useUpdateInspection = config =>
  useMutation(async ({ inspection, id }) => {
    const axiosResult = await getApi().put(`/inspections/${id}`, inspection);

    return axiosResult.data;
  }, config);

export const useUpdateInspectionStatus = config =>
  useMutation(async ({ id, status }) => {
    const response = await getApi().put(`/inspections/${id}/update_transition`, { status });

    return response.data;
  }, config);

export const useGetInspections = ({ clientId, params = {} }) => {
  const queryFn = async context => {
    const [{ clientId, params: queryParams }] = context.queryKey;

    const apiParams = {
      page: queryParams.page,
      per_page: queryParams.pageSize,
      ...(queryParams.query && { query: queryParams.query }),
      ...(isValidDateRange(queryParams.startDate, queryParams.endDate)
        ? {
            'inspected_at[from]': formatCalendarDate(queryParams.startDate),
            'inspected_at[to]': formatCalendarDate(queryParams.endDate)
          }
        : {}),
      ...(queryParams.status && { status: queryParams.status }),
      ...(queryParams.type && { inspection_type: queryParams.type }),
      ...(queryParams.formularyIds && { formulary_ids: [queryParams.formularyIds] }),
      ...(queryParams.projectIds && { project_ids: [queryParams.projectIds] }),
      ...(queryParams.inspectorIds && { inspector_ids: [queryParams.inspectorIds] }),
      ...(queryParams.published !== null && { published: Boolean(queryParams.published) })
    };

    const response = await getApi().get(`/clients/${clientId}/inspections`, {
      params: apiParams
    });

    const transformedData = transformSnake2Camel(response.data);

    return transformedData;
  };

  return useQuery({
    queryKey: [{ clientId, params }, 'GET_all_inspections'],
    queryFn,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
};

export const useGetInspection = ({ inspectionId, params = {} }, config) => {
  const queryFn = async context => {
    const [{ inspectionId, params: queryParams }] = context.queryKey;

    const response = await getApi().get(`/inspections/${inspectionId}`, { params: queryParams });

    return transformSnake2Camel(response.data);
  };

  return useQuery({
    queryKey: [{ inspectionId, params }, 'GET_inspection'],
    queryFn,
    enabled: !!inspectionId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...config
  });
};

export const useGetProjectFormularyInspections = ({ projectId, formularyId }, config) => {
  const queryFn = async context => {
    const [{ projectId, formularyId }] = context.queryKey;

    const response = await getApi().get(`/projects/${projectId}/formularies/${formularyId}/inspections`);

    return transformSnake2Camel(response.data);
  };

  return useQuery({
    queryKey: [{ projectId, formularyId }, 'GET_project_formulary_inspections'],
    queryFn,
    enabled: !!projectId && !!formularyId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...config
  });
};

export const useGetProjectInspections = ({ projectId }, config) => {
  const queryFn = async context => {
    const [{ projectId }] = context.queryKey;

    const response = await getApi().get(`/projects/${projectId}/inspections`);

    return transformSnake2Camel(response.data);
  };

  return useQuery({
    queryKey: [{ projectId }, 'GET_project_inspections'],
    queryFn,
    enabled: !!projectId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...config
  });
};

export const useGetFormularies = ({ clientId }, config) => {
  const queryFn = async context => {
    const [clientId] = context.queryKey;

    const response = await getApi().get(`/clients/${clientId}/formularies?include=none`);

    return transformSnake2Camel(response.data);
  };

  return useQuery({
    queryKey: [clientId, 'GET_all_formularies'],
    queryFn,
    enabled: !!clientId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...config
  });
};
